import { z } from 'zod';

/**
 * Onboarding step status. Per PRD-2:
 * - `completed`: Step is done (derived from observable facts)
 * - `skipped`: User clicked "ask me later" (persisted intent; still counts as outstanding)
 * - `incomplete`: Step is not done and not skipped
 */
export const OnboardingStepStatusSchema = z.enum(['completed', 'skipped', 'incomplete']);
export type OnboardingStepStatus = z.infer<typeof OnboardingStepStatusSchema>;

/**
 * Kind of step — core (built-in, required) or plugin (contributed, non-blocking).
 */
export const OnboardingStepKindSchema = z.enum(['core', 'plugin']);
export type OnboardingStepKind = z.infer<typeof OnboardingStepKindSchema>;

/**
 * Single step in the onboarding state view.
 */
export const OnboardingStepViewSchema = z.object({
  /** Unique stable identifier (e.g. `coding-agent-provider`, `github-linked`) */
  id: z.string(),
  /** Human-readable label */
  label: z.string(),
  /** Current status derived from facts + intent */
  status: OnboardingStepStatusSchema,
  /** Target route when user clicks to complete (e.g. `/admin/secrets`) */
  href: z.string(),
  /** Kind of step */
  kind: OnboardingStepKindSchema,
  /** Optional short description */
  description: z.string().optional(),
});
export type OnboardingStepView = z.infer<typeof OnboardingStepViewSchema>;

/**
 * Aggregate progress counts.
 */
export const OnboardingProgressSchema = z.object({
  /** Number of steps with status === 'completed' */
  completed: z.number().int().min(0),
  /** Number of steps with status === 'skipped' */
  skipped: z.number().int().min(0),
  /** Number of steps with status === 'incomplete' */
  incomplete: z.number().int().min(0),
  /** Total number of steps */
  total: z.number().int().min(0),
});
export type OnboardingProgress = z.infer<typeof OnboardingProgressSchema>;

/**
 * Full onboarding state view returned by `GET /user/onboarding/state`.
 *
 * @see docs/plans/user-onboarding-plan.md — PRD-11
 */
export const UserOnboardingStateViewSchema = z.object({
  /** Per-step status list */
  steps: z.array(OnboardingStepViewSchema),
  /** Aggregate progress counts (core steps only) */
  progress: OnboardingProgressSchema,
  /**
   * Timestamp (epoch ms) when the user first dismissed the onboarding wizard
   * (clicked "Continue to Agents"). NULL until dismissal. This is the
   * wizard-dismissal marker, not the all-steps-done marker; outstanding
   * steps continue to surface via the home banner.
   */
  firstCompletedAt: z.number().nullable(),
  /**
   * Whether the user has any outstanding steps (incomplete or skipped).
   * Used for progress tracking; does NOT directly control banner visibility.
   * Banner visibility is controlled by `showRequiredConfigurationBanner`.
   */
  hasOutstanding: z.boolean(),
  /**
   * Whether the required-configuration banner should be displayed.
   * Computed from `hasOutstanding` AND whether the user has dismissed the banner
   * at the current requirements fingerprint. When outstanding steps change
   * (new step, regressed step, status change), the fingerprint changes and
   * the banner reappears automatically.
   */
  showRequiredConfigurationBanner: z.boolean(),
});
export type UserOnboardingStateView = z.infer<typeof UserOnboardingStateViewSchema>;
